import { useEffect, useState } from 'react';
import { useStoreActions, useStoreState } from '../../../store';
import { SimulationWorkflowCard } from '../shared/SimulationWorkflowCard';
import { useSearchParams, Link } from 'react-router-dom-v5-compat';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { Button } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { partnerManagementService } from '~/services';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useQuery } from '@tanstack/react-query';
import { SelectCarrierRate } from '~/components/form/SelectCarrierRate';

export const CARRIER_RATE_SELECT_STEP_KEY = 'carrier-rate-select-step';
export interface CarrierRateSelectStepProps {
  showSellRates?: boolean;
}
export const CarrierRateSelectStep = ({ showSellRates = true }: CarrierRateSelectStepProps) => {
  const [selectedCarrierRateIds, setSelectedCarrierRateIds] = useState<string[] | undefined>();

  const fulfillmentContextLoading = useStoreState((state) => state.loading.loadingFlags.fulfillmentContext);
  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const updateSimulation = useStoreActions((actions) => actions.simulation.updateSimulation);
  const setSimulationSetupData = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const setActiveNavigationStep = useStoreActions((actions) => actions.simulation.setActiveNavigationStep);
  const activeNavigationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  // todo change to scotts sell rate ui when ready
  const sessionPartnerUrl = useStoreState((state) => state.session.user?.sessionPartnerUrl);
  const rateLandingLink = showSellRates ? `/${sessionPartnerUrl}/simulation/sell-rate-sheets` : 'https://sakara.com';

  const [startPolling, setStartPolling] = useState<boolean>();

  const {
    data: carrierRates = [],
    refetch: refetchCarrierRates,
    isLoading: isLoadingCarrierRates,
  } = useQuery({
    ...useQueryCreator(partnerManagementService.carrierRates.searchSummary, {
      args: showSellRates ? [{ carrierRateType: 'sell' }] : [],
    }),
    enabled: !!simulationId,
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    // once the user has navigated to the add sell rate page
    // we start polling for new sell rates
    if (startPolling) {
      interval = setInterval(() => {
        refetchCarrierRates();
      }, 5000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [startPolling, showSellRates, refetchCarrierRates]);

  useEffect(() => {
    if (simulationSetupData[CARRIER_RATE_SELECT_STEP_KEY]?.carrierRates?.length) {
      setSelectedCarrierRateIds(
        simulationSetupData[CARRIER_RATE_SELECT_STEP_KEY]?.carrierRates.map((cr) => cr.carrierRateId),
      );
    }
  }, [setSelectedCarrierRateIds, simulationSetupData]);
  const handleNext = async () => {
    if (simulationId) {
      // I could build a map but there won't be that
      const ratesWithDetails =
        selectedCarrierRateIds?.map((scri) => {
          const mapped = carrierRates.find((cr) => cr.carrierRateId === scri);
          return {
            name: mapped?.name || '',
            carrierId: mapped?.carrier || '',
            carrierServiceMethodId: mapped?.carrierServiceMethodId || '',
            carrierRateId: scri,
          };
        }) ?? [];
      const modifiedSetupData = {
        ...simulationSetupData,
        [CARRIER_RATE_SELECT_STEP_KEY]: { carrierRates: ratesWithDetails },
      };
      await updateSimulation({
        simulationId,
        simulationSetupData: modifiedSetupData,
      });
      setSimulationSetupData(modifiedSetupData);
      setActiveNavigationStep(activeNavigationStep + 1);
    }
  };

  return (
    <SimulationWorkflowCard
      loading={fulfillmentContextLoading}
      simulationId={simulationId ?? ''}
      onSave={handleNext}
      disabled={!selectedCarrierRateIds?.length}
    >
      <h3>Select a set of carrier rates to use as your sell rates for this simulation</h3>
      <Button
        onClick={() => {
          setStartPolling(true);
        }}
        type="primary"
        style={{ margin: 10 }}
      >
        <Link to={rateLandingLink} target="_blank" rel="noopener noreferrer">
          Add new sell ratesheets <PlusCircleOutlined />
        </Link>
      </Button>
      <SelectCarrierRate
        loading={isLoadingCarrierRates}
        value={selectedCarrierRateIds}
        carrierRates={carrierRates}
        validate
        onChange={setSelectedCarrierRateIds}
      />
    </SimulationWorkflowCard>
  );
};
